import React, { useState, useEffect } from 'react';
import { CreditCard, Zap, Check, ExternalLink, ShieldCheck, Download, AlertCircle, Calendar } from 'lucide-react';
import { API_BASE } from '../services/api';
import '../styles/billing.css';

function Billing({ onUpgradeClick, onUserUpdate }) {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [billingData, setBillingData] = useState(null);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    const initBilling = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        
        // 1. Verify checkout session if returning from Stripe
        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get('session_id');
        if (sessionId) {
          const verifyRes = await fetch(`${API_BASE}/stripe/verify-session`, {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}` 
            },
            body: JSON.stringify({ sessionId })
          });
          
          const verifyData = await verifyRes.json();
          if (verifyData.success) {
            localStorage.setItem('token', verifyData.token);
            const userStr = localStorage.getItem('user');
            if (userStr) {
              const userObj = JSON.parse(userStr);
              userObj.role = verifyData.role;
              if (verifyData.packageTier) {
                userObj.packageTier = verifyData.packageTier;
              }
              localStorage.setItem('user', JSON.stringify(userObj));
            }
            if (typeof onUserUpdate === 'function') {
              onUserUpdate(verifyData);
            }
          }
          window.history.replaceState({}, document.title, window.location.pathname);
        }

        // 2. Fetch billing info
        const response = await fetch(`${API_BASE}/stripe/billing-info`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!response.ok) throw new Error('Failed to fetch billing info');
        
        const data = await response.json();
        setBillingData(data);
        if (data?.plan?.billingCycle) {
          setBillingCycle(data.plan.billingCycle);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    initBilling();
  }, []);

  const handleManageBilling = async () => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/stripe/create-portal-session`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        }
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to open billing portal');
      }
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="billing-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <div className="app-loading-dots">
          <span></span><span></span><span></span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="billing-container">
        <div style={{ background: 'rgba(239, 68, 68, 0.1)', color: 'var(--danger, #ef4444)', padding: '1rem', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <AlertCircle size={22} />
          <span style={{ fontWeight: 600 }}>{error}</span>
        </div>
      </div>
    );
  }

  const { plan, paymentMethod, invoices } = billingData;

  return (
    <div className="billing-container">
      {/* Header section */}
      <div className="billing-header">
        <div>
          <h2 className="billing-title">Billing & Subscription</h2>
          <p className="billing-subtitle">
            Manage your workspace limits, update payment methods, and download past invoices.
          </p>
        </div>
        
        {/* Toggle (Only for non-enterprise) */}
        {plan.id !== 'enterprise' && !plan.isCustom && plan.id !== 'custom' && (
          <div className="billing-toggle-wrapper">
            <button 
              onClick={() => setBillingCycle('monthly')}
              className={`billing-toggle-btn ${billingCycle === 'monthly' ? 'active' : ''}`}
            >
              Monthly
            </button>
            <button 
              onClick={() => setBillingCycle('annual')}
              className={`billing-toggle-btn ${billingCycle === 'annual' ? 'active' : ''}`}
            >
              Annually 
              <span className="billing-toggle-badge">SAVE 20%</span>
            </button>
          </div>
        )}
      </div>
      
      <div className="billing-grid">
        {/* Current Plan Card - Premium Glass/Dark Design */}
        <div className="billing-plan-card">
          <div className="billing-plan-glow" />
          
          <div className="billing-active-badge" style={{
            background: (plan.status === 'active' || !plan.status) ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
            color: (plan.status === 'active' || !plan.status) ? '#34d399' : '#f87171',
            border: (plan.status === 'active' || !plan.status) ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(239, 68, 68, 0.3)'
          }}>
            {plan.status ? (plan.status.charAt(0).toUpperCase() + plan.status.slice(1)) : 'Active'} Plan
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', position: 'relative', zIndex: 1 }}>
            <div className="billing-plan-icon">
              <Zap size={22} color="#60a5fa" fill="rgba(96,165,250,0.2)" />
            </div>
            <h3 style={{ fontSize: '1.5rem', fontWeight: '700', margin: 0, letterSpacing: '-0.02em' }}>{plan.name}</h3>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: '0.25rem', position: 'relative', zIndex: 1 }}>
            <span className="billing-plan-price">
              {plan.isCustom ? 'Custom Allocated' : (billingCycle === 'monthly' ? plan.priceMonthly : plan.priceAnnual)}
            </span>
            {plan.id !== 'enterprise' && !plan.isCustom && <span style={{ fontSize: '1rem', color: '#94a3b8', fontWeight: '500', marginLeft: '0.35rem' }}>/{billingCycle === 'monthly' ? 'mo' : 'yr'}</span>}
          </div>
          
          {plan.id !== 'enterprise' && !plan.isCustom && (
            <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginBottom: '1.5rem', position: 'relative', zIndex: 1 }}>
              Billed {billingCycle === 'monthly' ? `monthly (${plan.priceMonthly})` : `annually (${plan.priceAnnual})`}
            </p>
          )}
          {plan.isCustom && (
            <p style={{ color: '#38bdf8', fontSize: '0.85rem', marginBottom: '1.5rem', position: 'relative', zIndex: 1, fontWeight: 500 }}>
              Dedicated resources managed directly by Platform Administrator
            </p>
          )}
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: 'auto', marginTop: plan.id === 'enterprise' ? '2rem' : '0', position: 'relative', zIndex: 1, paddingBottom: '2rem' }}>
            {plan.features.map((feature, i) => (
              <div key={i} className="billing-plan-feature">
                <Check size={16} color="#34d399" style={{ marginTop: '2px', flexShrink: 0 }} />
                <span>{feature}</span>
              </div>
            ))}
          </div>

          {plan.isCustom ? (
            <button disabled className="billing-plan-btn" style={{ opacity: 0.85, cursor: 'default', background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', border: '1px solid rgba(56, 189, 248, 0.3)' }}>
              • Custom Platform Allocation Active
            </button>
          ) : (
            <button onClick={onUpgradeClick} className="billing-plan-btn">
              Explore Other Plans
            </button>
          )}
        </div>

        {/* Payment Method Card - Clean, Bright, Premium */}
        <div className="billing-pm-card">
          <h3 className="billing-pm-details" style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--text-main)', margin: '0 0 0.5rem 0' }}>Payment Method</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '1.75rem', marginTop: 0 }}>
            Update your billing details securely via Stripe.
          </p>
          
          {paymentMethod ? (
            <div className="billing-pm-active">
              <div className="billing-pm-brand">
                <span>{paymentMethod.brand}</span>
              </div>
              <div className="billing-pm-details">
                <h4>
                  {paymentMethod.last4 && paymentMethod.last4 !== '••••' 
                    ? `•••• •••• •••• ${paymentMethod.last4}` 
                    : (paymentMethod.email || paymentMethod.brand || 'Connected Method')}
                </h4>
                {paymentMethod.exp_month && paymentMethod.exp_year ? (
                  <p>Expires {paymentMethod.exp_month}/{paymentMethod.exp_year}</p>
                ) : (
                  paymentMethod.email ? <p>{paymentMethod.email}</p> : <p>Default Payment Method</p>
                )}
              </div>
              <div style={{ background: 'rgba(16,185,129,0.1)', padding: '0.5rem', borderRadius: '50%' }}>
                <ShieldCheck size={20} color="#10b981" />
              </div>
            </div>
          ) : (
            <div className="billing-pm-empty">
              <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'var(--bg-main)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <CreditCard size={24} color="var(--text-muted)" />
              </div>
              <div style={{ color: 'var(--text-main)', fontWeight: '600', fontSize: '1rem', marginBottom: '0.25rem' }}>No payment method</div>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Add a card to activate your subscription</div>
            </div>
          )}

          {plan.isCustom ? (
            <div style={{ background: 'rgba(56, 189, 248, 0.08)', border: '1px solid rgba(56, 189, 248, 0.25)', padding: '0.85rem', borderRadius: '8px', marginBottom: '1.5rem', display: 'flex', alignItems: 'flex-start', gap: '0.6rem' }}>
              <ShieldCheck size={18} color="#38bdf8" style={{ marginTop: '2px', flexShrink: 0 }} />
              <p style={{ margin: 0, fontSize: '0.84rem', color: 'var(--text-main)', lineHeight: 1.45 }}>
                <strong>Platform Managed Workspace:</strong> Invoicing, quota provisioning, and billing cycles for this workspace are managed directly by your platform administrator.
              </p>
            </div>
          ) : (
            <>
              <div style={{ background: 'rgba(59, 130, 246, 0.08)', border: '1px solid rgba(59, 130, 246, 0.2)', padding: '0.75rem', borderRadius: '8px', marginBottom: '1.5rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                <AlertCircle size={16} color="#3b82f6" style={{ marginTop: '2px', flexShrink: 0 }} />
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#3b82f6', lineHeight: 1.4 }}>
                  <strong>Note:</strong> Subscription upgrades and downgrades made via the portal will be automatically prorated based on your current billing cycle.
                </p>
              </div>

              <button 
                disabled={actionLoading}
                onClick={handleManageBilling}
                className={`billing-action-btn ${paymentMethod ? '' : 'primary'}`}
              >
                <CreditCard size={18} /> {actionLoading ? 'Redirecting...' : paymentMethod ? 'Manage Billing Details' : 'Add Payment Method'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Allocated Resources Section */}
      {plan.limits && (
        <div className="billing-resources">
          <div className="billing-section-header">
            <div>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--text-main)', margin: '0 0 0.25rem 0', letterSpacing: '-0.01em' }}>Allocated Resources</h3>
              <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: '0.85rem' }}>Current workspace resource limits based on your active plan</p>
            </div>
          </div>
          <div className="billing-resources-grid">
            <div className="billing-resource-item">
              <span>Max Deployments</span>
              <span>{plan.limits.maxDeployments == null || plan.limits.maxDeployments < 0 ? 'Unlimited' : plan.limits.maxDeployments}</span>
            </div>
            <div className="billing-resource-item">
              <span>Team Members</span>
              <span>{plan.limits.maxTeamMembers == null || plan.limits.maxTeamMembers < 0 ? 'Unlimited' : plan.limits.maxTeamMembers}</span>
            </div>
            <div className="billing-resource-item">
              <span>CI/CD</span>
              <span>{plan.limits.cicdEnabled ? 'Enabled' : 'Disabled'}</span>
            </div>
            <div className="billing-resource-item">
              <span>Max Storage</span>
              <span>{plan.limits.maxStorageGB == null || plan.limits.maxStorageGB < 0 ? 'Unlimited' : plan.limits.maxStorageGB + ' GB'}</span>
            </div>
            <div className="billing-resource-item">
              <span>CPU / Container</span>
              <span>{plan.limits.maxCpuPerContainer == null ? 'N/A' : plan.limits.maxCpuPerContainer + ' Cores'}</span>
            </div>
            <div className="billing-resource-item">
              <span>RAM / Container</span>
              <span>{plan.limits.maxMemoryPerContainer || 'N/A'}</span>
            </div>
          </div>
        </div>
      )}

      {/* Invoice History */}
      <div className="billing-resources">
        <div className="billing-section-header">
          <div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--text-main)', margin: '0 0 0.25rem 0', letterSpacing: '-0.01em' }}>Billing History</h3>
            <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: '0.85rem' }}>View and download your past invoices</p>
          </div>
          {invoices.length > 0 && (
            <button className="billing-action-icon" style={{ borderRadius: '100px', padding: '0.5rem 1rem', gap: '0.4rem', background: 'var(--bg-main)', color: 'var(--text-main)', fontWeight: '500', fontSize: '0.85rem', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <Download size={14} /> Download All
            </button>
          )}
        </div>
        
        {invoices.length > 0 ? (
          <table className="billing-table">
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Date</th>
                <th>Amount</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice, i) => (
                <tr key={i}>
                  <td style={{ color: 'var(--text-main)', fontWeight: '600' }}>{invoice.id}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--text-muted)' }}>
                      <Calendar size={14} /> {invoice.date}
                    </div>
                  </td>
                  <td style={{ fontWeight: '700' }}>{invoice.amount}</td>
                  <td>
                    <span className={`billing-status-badge billing-status-${invoice.status.toLowerCase()}`}>
                      <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'currentColor' }} />
                      {invoice.status}
                    </span>
                  </td>
                   <td style={{ textAlign: 'right' }}>
                    <button 
                      disabled={!invoice.pdf}
                      onClick={() => invoice.pdf && window.open(invoice.pdf, '_blank')}
                      className="billing-action-icon"
                      title={invoice.pdf ? "Download PDF" : "PDF Unavailable"}
                    >
                      <ExternalLink size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={{ padding: '4rem 1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
            <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'var(--surface-light, rgba(0,0,0,0.03))', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.25rem' }}>
              <Download size={24} color="var(--text-muted)" />
            </div>
            <h4 style={{ color: 'var(--text-main)', fontSize: '1rem', fontWeight: '600', margin: '0 0 0.5rem 0' }}>No invoice history</h4>
            <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: '0.9rem', maxWidth: '300px' }}>
              You don't have any past invoices yet. Once a payment is processed, your receipts will appear here.
            </p>
          </div>
        )}
      </div>

    </div>
  );
}

export default Billing;

